import { useMemo, useState } from "react";

import {
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger
} from "@/components/ui/context-menu";
import { Input } from "@/components/ui/input";

interface Props extends React.ComponentProps<"div"> {
  rightClickedElement: HTMLElement;
}

type Direction = "top" | "right" | "bottom" | "left";

const directions: Array<{ key: Direction; label: string }> = [
  { key: "top", label: "بالا" },
  { key: "right", label: "راست" },
  { key: "bottom", label: "پایین" },
  { key: "left", label: "چپ" }
];

const parsePixels = (value: string) => {
  const parsedValue = Number.parseInt(value, 10);
  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

const ImageContextActions: React.FC<Props> = ({ rightClickedElement }) => {
  const imageContainer = useMemo(
    () =>
      (rightClickedElement.dataset.type === "image"
        ? rightClickedElement
        : rightClickedElement.closest('[data-type="image"]') ||
          rightClickedElement.closest("#image-wrapper")) as HTMLElement | null,
    [rightClickedElement]
  );

  const imageElement = useMemo(() => {
    if (!imageContainer) {
      return rightClickedElement.tagName.toUpperCase() === "IMG"
        ? (rightClickedElement as HTMLImageElement)
        : null;
    }

    return imageContainer.querySelector("img") as HTMLImageElement | null;
  }, [imageContainer, rightClickedElement]);

  const containerComputedStyle = imageContainer
    ? getComputedStyle(imageContainer)
    : null;

  const [size, setSize] = useState({
    width: imageContainer?.offsetWidth || 100,
    height: imageContainer?.offsetHeight || 100
  });

  const [margin, setMargin] = useState<Record<Direction, number>>({
    top: containerComputedStyle
      ? parsePixels(containerComputedStyle.marginTop)
      : 0,
    right: containerComputedStyle
      ? parsePixels(containerComputedStyle.marginRight)
      : 0,
    bottom: containerComputedStyle
      ? parsePixels(containerComputedStyle.marginBottom)
      : 0,
    left: containerComputedStyle
      ? parsePixels(containerComputedStyle.marginLeft)
      : 0
  });

  const [altText, setAltText] = useState(
    imageElement?.alt ?? imageElement?.title ?? ""
  );

  const updateSize = (direction: "width" | "height", value: string) => {
    if (!imageContainer) return;

    const nextValue = Math.min(
      2000,
      Math.max(20, Number.parseInt(value, 10) || 20)
    );

    setSize((prev) => ({ ...prev, [direction]: nextValue }));
    imageContainer.style[direction] = `${nextValue}px`;
  };

  const updateMargin = (direction: Direction, value: string) => {
    if (!imageContainer) return;

    const nextValue = Math.min(
      500,
      Math.max(0, Number.parseInt(value, 10) || 0)
    );

    setMargin((prev) => ({ ...prev, [direction]: nextValue }));
    imageContainer.style.setProperty(`margin-${direction}`, `${nextValue}px`);
  };

  const updateAltText = (value: string) => {
    setAltText(value);

    if (imageElement) {
      imageElement.alt = value;
      imageElement.title = value;
    }
  };

  const removeImage = () => {
    if (!imageContainer) return;

    const trailing = imageContainer.nextSibling;

    imageContainer.remove();

    if (trailing && trailing.nodeName === "BR" && trailing.parentElement) {
      trailing.parentElement.removeChild(trailing);
    }
  };

  return (
    <>
      <ContextMenuSub>
        <ContextMenuSubTrigger>اندازه عکس</ContextMenuSubTrigger>
        <ContextMenuSubContent className="w-72 space-y-1 p-1">
          {[
            { key: "width", label: "عرض", max: 2000 },
            { key: "height", label: "ارتفاع", max: 2000 }
          ].map((field) => (
            <div
              key={field.key}
              title={`اندازه ${field.label}`}
              className="hover:bg-muted/60 grid grid-cols-[48px_62px_1fr] items-center gap-1 rounded-sm px-1 py-1"
            >
              <span className="text-muted-foreground text-[11px]">
                {field.label}
              </span>
              <Input
                type="number"
                className="h-7 px-2 text-xs"
                min={20}
                max={field.max}
                step={1}
                value={size[field.key as "width" | "height"]}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) =>
                  updateSize(field.key as "width" | "height", e.target.value)
                }
              />
              <Input
                type="range"
                className="h-4 border-none p-0"
                min={20}
                max={field.max}
                step={1}
                value={size[field.key as "width" | "height"]}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) =>
                  updateSize(field.key as "width" | "height", e.target.value)
                }
              />
            </div>
          ))}
        </ContextMenuSubContent>
      </ContextMenuSub>

      <ContextMenuSub>
        <ContextMenuSubTrigger>مارجین عکس</ContextMenuSubTrigger>
        <ContextMenuSubContent className="w-72 space-y-1 p-1">
          {directions.map((direction) => (
            <div
              key={direction.key}
              title={`مارجین ${direction.label}`}
              className="hover:bg-muted/60 grid grid-cols-[48px_62px_1fr] items-center gap-1 rounded-sm px-1 py-1"
            >
              <span className="text-muted-foreground text-[11px]">
                {direction.label}
              </span>
              <Input
                type="number"
                className="h-7 px-2 text-xs"
                min={0}
                max={500}
                step={1}
                value={margin[direction.key]}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => updateMargin(direction.key, e.target.value)}
              />
              <Input
                type="range"
                className="h-4 border-none p-0"
                min={0}
                max={500}
                step={1}
                value={margin[direction.key]}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => updateMargin(direction.key, e.target.value)}
              />
            </div>
          ))}
        </ContextMenuSubContent>
      </ContextMenuSub>

      <ContextMenuSub>
        <ContextMenuSubTrigger>متن جایگزین (alt)</ContextMenuSubTrigger>
        <ContextMenuSubContent
          className="w-64 p-2"
          onClick={(e) => e.stopPropagation()}
        >
          <Input
            placeholder="متن alt"
            value={altText}
            onChange={(e) => updateAltText(e.target.value)}
          />
        </ContextMenuSubContent>
      </ContextMenuSub>

      <ContextMenuSeparator />
      <ContextMenuItem className="text-red-500" onClick={removeImage}>
        حذف عکس
      </ContextMenuItem>
    </>
  );
};

export default ImageContextActions;
